import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { dateMethods } from '@utils/dates/dateMethods';
import { processCommon } from '@utils/dashboard/processCommon';
import AlertMessage from './AlertMessage';
import {
    APP_STATUSES,
    APP_TYPES,
    ALERT_TYPES,
    ALERT_MESSAGES_NAMES,
    FORMATTED_DATES,
    VERIFICATION_STATUSES,
    DMI_DOCUMENT_UPLOAD_STATUS,
    EVENT_NAMES
} from '@constants/individualPortal/dashboard/dashboardModelTypes';

import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import { copyApplication } from '@actions/individualPortal/dashboard/dashboardActionCreators';
import { configs } from '@constants/configs/configs';
import { timezoneAbbreviations } from '@constants/common/timezone';
import { DATE_FORMAT } from '@utils/dates/dateMethods';
import {
    addYearToDateAndMonth,
    convertDateToUtcAndTimeZone,
    formatDate,
    getFormattedLocaleDate,
    getYearInNumber,
    isDateBetween,
    isDateSameOrAfter,
    isDateSameOrBefore,
    isDateValid,
    parseDateWithFormat,
} from '@utils/dates/date.utils';
import {
    MM_SLASH_DD_SLASH_YYYY,
} from '@utils/dates/dateFormats.utils';
import dayjs from 'dayjs';
import { history } from '@store/appStore';
import { useExchangeConditions } from '@hooks/index';
import { IEX_STATE_CODES } from '@constants/configs/states';

const AlertMessages = (props) => {
    const {
        environment: {
            language,
            stateCode,
            exchangeName,
            exchangeStartYear,
            tobaccoEnabled,
            hardshipEnabled,
            isState,
            isMNFullStack,
            lowFplOnceAMonthShoppingThreshold,
            localDates: {
                oepStartDate,
                oepEndDate,
                oepEnrollEndDate,
                exceptionalCircumstancesStartDate,
                exceptionalCircumstancesEndDate,
                globalConfigCoverageStartDate,
                timezone,
                serverDateInUTC,
            },
            flags: {
                isOeBannerEnabled,
                isOeEventBannerEnabled,
                lowFplToEnableOnceAMonthShopping,
            },
            links: {
                HELP,
                PHONE,
            }
        },
        dashboardModels,
        activeTab,
        copyApplication
    } = props;

    const {
        endpoints,
        externalEndpoints,
        routes
    } = configs;

    const activeTabData = dashboardModels[activeTab];
    const prevTabYear = dashboardModels[activeTab - 1] ? dashboardModels[activeTab - 1].applicationYear : null;
    const { isOEApproaching, isOEEndDateApproaching, isExtendedOEPDates, getFormattedDate, isExceptionalCircumstances } = dateMethods;

    const {
        isAppStatusesEnrolled,
        checkApplicantEventName,
        checkChangeType,
        getCountChangeType,
        hasEnrollments,
        isAIAN,
        isQhpEligibleAIAN,
        isAllAIANHouseholdWithSomeQhpEligible,
        checkFPL
    } = processCommon;

    const {
        applicationYear,
        applicationStatus,
        applicationDentalStatus,
        applicationType,
        applicants,
        aptc,
        stateSubsidy,
        isInsideOEWindow,
        isInsideOEEnrollmentWindow,
        isLocked,
        isSepOpen,
        sepStartDate,
        sepEndDate,
        caseNumber,
        enrollments,
        verificationStatus,
        dmiDocumentUploadstatus,
        encryptedCaseNumber,
        fpl,
        sepEvent
    } = activeTabData;
    const { shouldRedirectToCustomGrouping, isEnhancedAianShoppingBannerHidden, isStateHidingAianBannerDuringOE } = useExchangeConditions();

    const CURRENT_YEAR = getFormattedDate(FORMATTED_DATES.currentYear, applicationYear);
    const NEXT_YEAR = getFormattedDate(FORMATTED_DATES.nextYear, applicationYear, isInsideOEWindow);
    const PREVIOUS_YEAR = getFormattedDate(FORMATTED_DATES.previousYear, applicationYear);

    const appStatusEN = isAppStatusesEnrolled(applicationStatus, applicationDentalStatus);
    const is_ER_SEP = applicationStatus === APP_STATUSES.ER && applicationType === APP_TYPES.SEP;

    const zone = timezone ? timezoneAbbreviations[timezone] : dayjs.tz.setDefault();
    const formattedServerDate = formatDate(convertDateToUtcAndTimeZone(serverDateInUTC, zone), DATE_FORMAT);
    const enabledARPA = true;

    // List of all alert messages.
    const alertMessages = [];

    const addAlertMessage = ( name, type, noticeText, showIcon ) => {
        alertMessages.push({ name, type, noticeText, showIcon });
    };

    const getFormattedMessageById = id => 
        <FormattedMessage 
            id={id}
            values={{
                br: <br/>,
                strong: chunks => <strong>{chunks}</strong>
            }}
        />;

    const getOEApproachingMessage = () => {

        return (<FormattedMessage
            id="indportal.dashboard.process_warnings.oe_approaching"
            values={{
                currentYear: CURRENT_YEAR,
                externalLink: !isState.NM ? '' : <a
                    style={{ textDecoration: 'none' }}
                    href={externalEndpoints.NM_LINKS.BEWELL_APPT}
                    rel="noopener noreferrer"><FormattedMessage id="indportal.dashboard.process_warnings.oe_approaching.text_link"/>
                </a>,
                nextYear: NEXT_YEAR,
                oepStartDate: getFormattedDate(FORMATTED_DATES.oepStartDate, oepStartDate),
                oepEndDate: getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate),
                oeYear: getFormattedDate(FORMATTED_DATES.oeYear, oepStartDate),
                br: <br/>,
                strong: chunks => <strong>{chunks}</strong>
            }}
        />);
    };

    //Hard Values for OE_END_DATE_APPROACHING
    const NOVEMBER_ELEVEN  = formatDate(addYearToDateAndMonth('11/01/', PREVIOUS_YEAR), DATE_FORMAT);
    const DECEMBER_FIFTEEN = formatDate(addYearToDateAndMonth('12/15/', PREVIOUS_YEAR), DATE_FORMAT);
    const DECEMBER_SIXTEEN = formatDate(addYearToDateAndMonth('12/16/', PREVIOUS_YEAR), DATE_FORMAT);
    const JANUARY_1ST      = formatDate(addYearToDateAndMonth('01/01/', NEXT_YEAR), DATE_FORMAT);
    const FEBRUARY_1ST     = formatDate(addYearToDateAndMonth('02/01/', NEXT_YEAR), DATE_FORMAT);

    const isConfirmLifeEvent =
        (   
            isDateSameOrAfter(formattedServerDate, DECEMBER_SIXTEEN) &&
            isDateSameOrBefore(formattedServerDate, getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate)) &&
            isDateSameOrAfter(getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate), JANUARY_1ST) &&
            isOeEventBannerEnabled &&
            (
                (applicationStatus === APP_STATUSES.ER && sepEvent === null) ||
                (sepEvent !== null && sepEvent.isIdentified)
            )
        );
    const isChangeDuringOEP = applicants ? applicants.some(applicant => applicant.sepApplicantEvents ? applicant.sepApplicantEvents.some(item => item.eventName === 'CHANGE_DURING_OEP') : null ) : null;
    const isBetweenNovDecDate = ( 
        isDateSameOrAfter(formattedServerDate, NOVEMBER_ELEVEN) && 
        isDateSameOrBefore(formattedServerDate, DECEMBER_FIFTEEN)
    );
    const globalStartDate = 
        isDateValid(globalConfigCoverageStartDate)
            ? getFormattedDate(FORMATTED_DATES.globalConfigCoverageStartDate, globalConfigCoverageStartDate) 
            : isBetweenNovDecDate ?  JANUARY_1ST : FEBRUARY_1ST;

    //If Confirm Life Event is Enabled used specific conditions, else use original condition

    const isFullyEnrolled = applicationStatus === APP_STATUSES.EN && applicationDentalStatus === APP_STATUSES.EN;

    const legacyOEDateApproachingConditions = (isOeBannerEnabled && isInsideOEWindow && (applicationType === APP_TYPES.OE || !applicationType) && !isFullyEnrolled);
    const confirmLifeEventConditions = (isOeBannerEnabled && isInsideOEWindow && !isFullyEnrolled ) && (sepEvent === null || (sepEvent !== null && sepEvent.isIdentified && isChangeDuringOEP) );
    const oEApproachingConditions  = isOeEventBannerEnabled ? confirmLifeEventConditions : legacyOEDateApproachingConditions;

    const formattedOepStartDate = getFormattedDate(FORMATTED_DATES.oepStartDate,oepStartDate);
    const formattedOepEnrollEndDate = getFormattedDate(FORMATTED_DATES.oepEnrollEndDate, oepEnrollEndDate);
    const extendedOECoverageConditions =
        isOeBannerEnabled &&
        !prevTabYear &&
        isDateBetween(formattedServerDate, formattedOepStartDate, formattedOepEnrollEndDate, 'day', '[]');

    const getOEEndDateApproachingMessage = () => {
        let days = oepEndDate.diff(formattedServerDate, 'days');
        const HEALTH_CARE_GOV_LINK = externalEndpoints.GENERAL.HEALTH_CARE_GOV;
        const CONNECT_LINK = externalEndpoints.PA_LINKS.CONNECT;
        const confirmLifeEventMsg = isConfirmLifeEvent && (prevTabYear >= exchangeStartYear) && <FormattedMessage
            id="indportal.dashboard.process_warnings.oe_end_date_approaching_message_confirm_Life_event"
            values={{
                globalStartDate: isState.PA ? getFormattedLocaleDate(globalStartDate, language, 'LL') : globalStartDate,
                externalLink: <a
                    style={{ textDecoration: 'none' }}
                    href={endpoints.LCE_REFERRAL.processReplacements({ encryptedCaseNumber })}
                    rel="noopener noreferrer"><FormattedMessage id="label.link.confirmLifeEvent"/>
                </a>,
                lineBreak: <br />,
            }}
        />;
        return <FormattedMessage
            id="indportal.dashboard.announcements.warning.oeEndDateApproachingMessage"
            values={{
                lineBreak: <br />,
                oepStartDate: getFormattedDate(FORMATTED_DATES.oepStartDate, oepStartDate),
                oepEndDate: getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate),
                oeDays: days,
                oeDaysPlusOne: days + 1,
                oepEnrollEndDate: getFormattedDate(FORMATTED_DATES.oepEnrollEndDate, oepEnrollEndDate),
                nextYear: NEXT_YEAR,
                prevTabYear: prevTabYear >= exchangeStartYear ?
                    <FormattedMessage
                        id="indportal.dashboard.process_warnings.oe_end_date_approaching_message_prev_tab"
                        values={{
                            linebreak: <br />,
                            year: prevTabYear,
                            currentYear: CURRENT_YEAR,
                            previousYear: PREVIOUS_YEAR }}
                    /> : '',
                visitExternalMsg: !prevTabYear && <FormattedMessage id="indportal.dashboard.process_warnings.oe_end_date_approaching_message_external_msg"
                    values={{
                        previousYear: PREVIOUS_YEAR,
                        externalLink: <a style={{ textTransform: 'lowercase' }} href={HEALTH_CARE_GOV_LINK} target="_blank" rel="noopener noreferrer"> healthcare.gov </a>,
                        confirmLifeEvent: confirmLifeEventMsg,
                        exchangeName,
                        exchangePhone: PHONE,
                    }}/>,
                externalConnectLink: <a href={CONNECT_LINK} target="_blank" rel="noopener noreferrer">{CONNECT_LINK}</a>,
                currentYear: CURRENT_YEAR,
                previousYear: PREVIOUS_YEAR,
                confirmLifeEvent: confirmLifeEventMsg,
                exchangeName,
                exchangePhone: PHONE
            }}/>;
    };

    const getSEPRemainingDays = () => {
        const endData = formattedServerDate;
        const startDate = parseDateWithFormat(sepEndDate, MM_SLASH_DD_SLASH_YYYY);
        let days = startDate.diff(endData, 'days');
        if ( days < 0 ) {
            return null;
        }
        if ( days === 0 ) {
            days = days + 1;
        }
        return days;
    };

    const getSEPDateMessage = () => {
        const days = getSEPRemainingDays();
        if (days === null) return null;
        return <FormattedMessage
            id="indportal.dashboard.process_warnings.sep_ending"
            values={{ sepDays: days }}/>;
    };

    const uploadDocumentsHandler = (e) => {
        e.preventDefault();
        ctaHandlers.goToUploadDocs(caseNumber, stateCode, isMNFullStack);
    };

    const getDMIFailureMessage = (dmiDocumentUploadstatus) => {

        switch(dmiDocumentUploadstatus) {
            case DMI_DOCUMENT_UPLOAD_STATUS.PENDING:
                return (
                    <FormattedMessage
                        id="indportal.dashboard.announcements.warnings.dmiFailure"
                        values={{
                            uploadDocuments: (
                                <a href="#" style={{ textTransform: 'lowercase' }} onClick={uploadDocumentsHandler} rel="noopener noreferrer">
                                    <FormattedMessage id="further_action.cta.btn_upload_documents" />
                                </a>
                            ),
                            uploadDocumentsLink : (chunks) => <a href="#" onClick={uploadDocumentsHandler} rel="noopener noreferrer">{chunks}</a>
                        }}
                    />
                );
            case DMI_DOCUMENT_UPLOAD_STATUS.PROCESSING:
                return <FormattedMessage id="indportal.dashboard.process_warnings.DMI_failure_pending"/>;
        }

    };

    const getNewMemberAddedMessage = () => {
        const cntNewMembers = getCountChangeType(applicants, 'ADD');
        return <FormattedMessage id="indportal.dashboard.process_warnings.new_member_added" values={{ cntNewMembers }}/>;
    };

    const getExtendedOEPMessage = () => {
        return (<FormattedMessage
            id="indportal.dashboard.process_warnings.extended_OEP"
            values={{
                oepEndDate: getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate),
                oepEnrollEndDate: getFormattedDate(FORMATTED_DATES.oepEnrollEndDate, oepEnrollEndDate)
            }}
        />);
    };

    const getExtendedOECoverageMessage = () => {
        return (<FormattedMessage
            id="indportal.dashboard.announcements.info.oeEnrollChangeCurrentYearMessage"
            values={{
                nextYear: NEXT_YEAR,
                currentYear: CURRENT_YEAR
            }}
        />);
    };
    const getAPTCChangeMessage = (totalAPTC, totalSPTC = 0, applicants) => {
        const isAPTCEligible = applicants.some(applicant => applicant.aptcEligible);
        const isSPTCEligible = applicants.some(applicant => applicant.stateSubsidyEligible);
        const isCSREligible = applicants.some(applicant => applicant.csrEligible);
        const andOrMessage = isAPTCEligible ? <FormattedMessage id="indportal.dashboard.process_warnings.and_or"/> : '';

        if (!isAPTCEligible && !isSPTCEligible && !isCSREligible) return false;
        return <FormattedMessage
            id="indportal.dashboard.process_warnings.aptc_change"
            values={{
                isAPTCEligible: isAPTCEligible || isSPTCEligible ?
                    <FormattedMessage id="indportal.dashboard.process_warnings.aptc_eligible"
                        values={{ totalSubsidy: (totalAPTC + totalSPTC).toFixed(2) }}/> : '',
                isCSREligible: isCSREligible ?
                    <FormattedMessage id="indportal.dashboard.process_warnings.csr_eligible" values={{ andOr: andOrMessage }}/> : ''
            }}/>;
    };

    const hideSepCountMsg = () => {
        return applicationStatus === APP_STATUSES.EN && applicationDentalStatus === APP_STATUSES.EN &&
            (enrollments && enrollments.length > 0 ? enrollments.every((enrollment) => enrollment.allowChangePlan === false) : true);
    };

    const onExceptionalEditAppHandler = (e) => {
        e.preventDefault();
        const isTobaccoInformationAvailableForAllUser = !applicants?.some(applicant => [null, undefined, 'undefined'].includes(applicant.tobacco));
        ctaHandlers.goToExceptionalEditApplication(caseNumber, copyApplication, { tobaccoEnabled, hardshipEnabled, history, applicationStatus, isTobaccoInformationAvailableForAllUser, shouldRedirectToCustomGrouping });
    };

    const getExceptionalCircumstancesMessage = () => {
        return (<FormattedMessage
            id="indportal.dashboard.process_warnings.exceptionalCircumstances"
            values={{
                exchangeName,
                stateCode,
                exceptionalCircumstancesEndDate: getFormattedDate(FORMATTED_DATES.exceptionalCircumstancesEndDate, exceptionalCircumstancesEndDate),
                exceptionalCircumstancesEndDateMMMMDo: getFormattedDate(FORMATTED_DATES.exceptionalCircumstancesEndDateMMMMDo, exceptionalCircumstancesEndDate),
                exceptionalCircumstancesEndDateESFormat: getFormattedDate(FORMATTED_DATES.exceptionalCircumstancesEndDateESFormat, exceptionalCircumstancesEndDate),
                link: <a href="#" onClick={onExceptionalEditAppHandler} rel="noopener noreferrer">
                    <FormattedMessage id='indportal.dashboard.process_warnings.exceptionalCircumstances_link'/>
                </a>

            }}
        />);
    };

    const getARPAMessage = () => {
        return (<FormattedMessage
            id="indportal.dashboard.process_warnings.ARPA"
            values={{
                exchangeName,
                linkEnrollments: <a href={routes.MY_ENROLLMENT_HISTORY} rel="noopener noreferrer"><FormattedMessage id="common.here"/></a>,
                linkFAQ: <a href={HELP} target="_blank" rel="noopener noreferrer"><FormattedMessage id="common.plan.ARPA"/></a>,
                linkFAQ_PA: <a href={externalEndpoints.PA_LINKS.FAQ} target="_blank" rel="noopener noreferrer">Pennie.com</a>,
                linebreak: <br />,
                ARP: <FormattedMessage id="common.ARP" tagName="b"/>,
                select: <FormattedMessage id="common.select" tagName="b"/>
            }}
        />);
    };

    const getLowFPLMessage = () => {
        return (<FormattedMessage
            id="indportal.dashboard.process_warnings.low_FPL"
            values={{
                exchangeName,
                confirmLifeEventLink: <a
                    href={endpoints.LCE_REFERRAL.processReplacements({ encryptedCaseNumber })}
                    rel="noopener noreferrer"><FormattedMessage id="common.click_here"/>
                </a>
            }}
        />);
    };

    const handleExceptionsForStates = () => {
        switch(true) {
            case isState.PA:
                return false;
            case isState.ID:
                return CURRENT_YEAR === getYearInNumber(oepStartDate);
            default:
                return true;
        }
    };

    const getAIANPrivilegeMessage = (showAllHouseholdVariant) => {
        const messageId = showAllHouseholdVariant
            ? 'indportal.dashboard.process_warnings.aianAllHouseholdPrivilege'
            : 'indportal.dashboard.process_warnings.aianPrivilege';
        const sepDays = getSEPRemainingDays() ?? 0;
        return <FormattedMessage id={messageId} values={{ sepDays }} />;
    };

    const getMixedHouseholdSEPMessage = () => {
        const days = getSEPRemainingDays();
        const isNonAIANMembersTextVisible = days !== null &&
            (stateCode !== IEX_STATE_CODES.NV || !isInsideOEEnrollmentWindow);
        return (
            <>
                {getAIANPrivilegeMessage(false)}
                <ConditionalRendering isTrue={isNonAIANMembersTextVisible}>
                    <div>
                        <FormattedMessage
                            id="indportal.dashboard.process_warnings.sepNonAianMixedHousehold"
                            values={{ sepDays: days, br: <br /> }}
                        />
                    </div>
                </ConditionalRendering>
            </>
        );
    };

    const isAllAianHouseholdEligibleForPrivilege = isAllAIANHouseholdWithSomeQhpEligible(applicants);
    const isMixedHousehold = isQhpEligibleAIAN(applicants) && !isAllAianHouseholdEligibleForPrivilege;
    const hasQhpEligibleAianMember = isAllAianHouseholdEligibleForPrivilege || isMixedHousehold;
    const isEnhancedAianBannerSuppressed =
        isEnhancedAianShoppingBannerHidden || (isStateHidingAianBannerDuringOE && isInsideOEEnrollmentWindow);
    const shouldShowAianPrivilegeAlert = !isEnhancedAianBannerSuppressed && !isSepOpen && hasQhpEligibleAianMember;
    /*************************
     # ALERT_MESSAGES
     *************************/

    // 1) OE_APPROACHING
    if (!isInsideOEEnrollmentWindow && isOEApproaching(formattedServerDate, oepStartDate) && handleExceptionsForStates()) {
        const text =  getOEApproachingMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.OE_APPROACHING, ALERT_TYPES.INFO, text, true );
    }

    // 2) EXTENDED_OE_COVERAGE (PA only!)
    if (isState.PA && extendedOECoverageConditions) {
        const text = getExtendedOECoverageMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.EXTENDED_ENROLL_OE, ALERT_TYPES.INFO, text, false );
    }

    // 3) OE_END_DATE_APPROACHING
    if (oEApproachingConditions) {
        if (isOEEndDateApproaching(formattedServerDate, oepStartDate, oepEndDate)) {
            const text = getOEEndDateApproachingMessage();
            addAlertMessage(ALERT_MESSAGES_NAMES.OE_END_DATE_APPROACHING, ALERT_TYPES.INFO, text, true );
        }
    }

    // 4) AIAN_PRIVILEGE
    if (shouldShowAianPrivilegeAlert) {
        const text = getAIANPrivilegeMessage(isAllAianHouseholdEligibleForPrivilege);
        addAlertMessage(ALERT_MESSAGES_NAMES.AIAN_PRIVILEGE, ALERT_TYPES.WARNING, text, false);
    }

    // 5) SEP_DATE
    if (isSepOpen && !hideSepCountMsg() && sepStartDate && sepEndDate) {
        if (isEnhancedAianBannerSuppressed) {
            const sepDateText = getSEPDateMessage();
            sepDateText && addAlertMessage(ALERT_MESSAGES_NAMES.SEP_DATE, ALERT_TYPES.WARNING, sepDateText, true);
        } else if (isAllAianHouseholdEligibleForPrivilege) {
            const text = getAIANPrivilegeMessage(isAllAianHouseholdEligibleForPrivilege);
            addAlertMessage(ALERT_MESSAGES_NAMES.AIAN_PRIVILEGE, ALERT_TYPES.WARNING, text, false);
        } else if (isMixedHousehold) {
            const text = getMixedHouseholdSEPMessage();
            addAlertMessage(ALERT_MESSAGES_NAMES.SEP_NON_AIAN_MIXED_HOUSEHOLD, ALERT_TYPES.WARNING, text, false);
        } else {
            const sepDateText = getSEPDateMessage();
            if (sepDateText) {
                if (stateCode === IEX_STATE_CODES.NV) {
                    !isInsideOEEnrollmentWindow &&
                        addAlertMessage(ALERT_MESSAGES_NAMES.SEP_DATE, ALERT_TYPES.WARNING, sepDateText, true);
                } else {
                    addAlertMessage(ALERT_MESSAGES_NAMES.SEP_DATE, ALERT_TYPES.WARNING, sepDateText, true);
                }
            }
        }
    }

    // 6) DMI_FAILURE
    if (dmiDocumentUploadstatus && verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED) {
        const text = getDMIFailureMessage(dmiDocumentUploadstatus, caseNumber);
        addAlertMessage(ALERT_MESSAGES_NAMES.DMI_FAILURE, ALERT_TYPES.WARNING, text, false );
    }

    // 7) NEW_MEMBER_ADDED
    if (isSepOpen && is_ER_SEP && checkChangeType(applicants, 'ADD')) {
        const text = getNewMemberAddedMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.NEW_MEMBER_ADDED, ALERT_TYPES.INFO, text, false );
    }

    // 8) MEMBER_REMOVED
    if (isSepOpen && is_ER_SEP && checkChangeType(applicants, 'REMOVE')) {
        const text = getFormattedMessageById('indportal.dashboard.process_warnings.member_removed');
        addAlertMessage(ALERT_MESSAGES_NAMES.MEMBER_REMOVED, ALERT_TYPES.INFO, text, false );
    }

    // 9) APTC_CHANGE
    if (isSepOpen && is_ER_SEP && checkApplicantEventName(applicants, EVENT_NAMES.INCOME_CHANGE_VALUES)) {
        const text = getAPTCChangeMessage(aptc, stateSubsidy, applicants);
        text && addAlertMessage(ALERT_MESSAGES_NAMES.APTC_CHANGE, ALERT_TYPES.INFO, text, false );
    }


    // 10) EXTENDED_OEP (NV only!)
    if (stateCode === IEX_STATE_CODES.NV && applicationType === APP_TYPES.OE &&
            isExtendedOEPDates(formattedServerDate, oepEndDate, oepEnrollEndDate) && !appStatusEN) {
        const text = getExtendedOEPMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.EXTENDED_OEP, ALERT_TYPES.WARNING, text, false );
    }

    // 11) Exceptional Circumstances
    if ((isState.NJ || isState.PA) &&
        isExceptionalCircumstances(formattedServerDate, exceptionalCircumstancesStartDate, exceptionalCircumstancesEndDate) &&
        hasEnrollments(enrollments) && !isSepOpen
    ) {
        const text = getExceptionalCircumstancesMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.EXCEPTIONAL_CIRCUMSTANCES, ALERT_TYPES.INFO, text, false );
    }

    // 12) ARPA - temp alert only for 2021 applicationYear, will be removed later.
    if (applicationYear === '2021' && isState.PA && enabledARPA) {
        const text = getARPAMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.ARPA, ALERT_TYPES.INFO, text, false );
    }

    // 13) LOW FLP
    if ((isState.NJ || isState.PA) && applicationStatus === APP_STATUSES.ER && applicationType === APP_TYPES.QEP &&
        !isAIAN(applicants) && checkFPL(fpl, applicants, lowFplToEnableOnceAMonthShopping, lowFplOnceAMonthShoppingThreshold)) {
        const text = getLowFPLMessage();
        addAlertMessage(ALERT_MESSAGES_NAMES.LOW_FPL, ALERT_TYPES.WARNING, text, false );
    }

    // 14) MAGI_LOCK
    if (isState.ID && isLocked) {
        const text = <FormattedMessage
            id="process_warnings.locked"
            values={{
                eligibilityLink: <Link to={
                    {
                        pathname: configs.routes.ELIGIBILITY_RESULTS,
                        state: {
                            prevLocation: configs.routes.INDPORTAL,
                            encryptedCaseNumber: activeTabData.encryptedCaseNumber
                        }
                    }
                }>
                    <FormattedMessage id="common.here"/>
                </Link>,
                linebreak: <br/>,
                designateLink: (
                    <a
                        href={externalEndpoints.ID_LINKS.DESIGNATE_REPRESENTATIVE}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {externalEndpoints.ID_LINKS.DESIGNATE_REPRESENTATIVE}
                    </a>
                ),
            }}
        />;
        addAlertMessage(ALERT_MESSAGES_NAMES.LOCKED, ALERT_TYPES.WARNING, text, false );
    }
    
    return ( alertMessages && Object.keys(alertMessages).length > 0 &&
        <div id="auto-warnings" className="gi-alert-panel margin-b-10">
            {
                alertMessages.map((warning, index) => {
                    return <AlertMessage key={index}
                        content={warning.noticeText}
                        type={warning.type}
                        showIcon={warning.showIcon}
                    />;
                })
            }
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab
});


export default connect(mapStateToProps, { copyApplication })(AlertMessages);